import * as React from 'react';

/** Keep reading stable while new turns arrive or an earlier bounded window is loaded. */
export function useAgentScroll(contentVersion: unknown, isSending = false, error?: unknown) {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const following = React.useRef(true);
  const prepend = React.useRef<{ id?: string; top: number; height: number; scrollTop: number } | null>(null);
  const [showJumpToLatest, setShowJumpToLatest] = React.useState(false);
  const jumpToLatest = React.useCallback(() => {
    const viewport = viewportRef.current;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
    following.current = true;
    prepend.current = null;
    setShowJumpToLatest(false);
  }, []);
  const handleScroll = React.useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    following.current = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 120;
    if (prepend.current) prepend.current = captureAnchor(viewport);
    setShowJumpToLatest(!following.current);
  }, []);
  const prepareEarlier = React.useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    prepend.current = captureAnchor(viewport);
    following.current = false;
  }, []);
  const cancelEarlier = React.useCallback(() => { prepend.current = null; }, []);
  const reset = React.useCallback(() => {
    prepend.current = null;
    following.current = true;
    setShowJumpToLatest(false);
  }, []);
  React.useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const previous = prepend.current;
    if (previous) {
      const anchor = [...viewport.querySelectorAll<HTMLElement>('[data-agent-turn-id]')]
        .find(element => element.dataset.agentTurnId === previous.id);
      viewport.scrollTop = anchor ? viewport.scrollTop + anchor.getBoundingClientRect().top - previous.top
        : previous.scrollTop + viewport.scrollHeight - previous.height;
      prepend.current = null;
      handleScroll();
    } else if (following.current) jumpToLatest();
    else setShowJumpToLatest(true);
  }, [contentVersion, isSending, error, handleScroll, jumpToLatest]);
  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => { if (following.current) jumpToLatest(); });
    observer.observe(viewport);
    if (viewport.firstElementChild) observer.observe(viewport.firstElementChild);
    return () => observer.disconnect();
  }, [jumpToLatest, contentVersion]);
  return { viewportRef, showJumpToLatest, jumpToLatest, handleScroll, prepareEarlier, cancelEarlier, reset };
}

function captureAnchor(viewport: HTMLDivElement) {
  const top = viewport.getBoundingClientRect().top;
  const anchor = [...viewport.querySelectorAll<HTMLElement>('[data-agent-turn-id]')]
    .find(element => element.getBoundingClientRect().bottom > top);
  return { id: anchor?.dataset.agentTurnId, top: anchor?.getBoundingClientRect().top ?? top,
    height: viewport.scrollHeight, scrollTop: viewport.scrollTop };
}
